import { Box, Stack } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { getLabelByTeamCategoryKind } from "@components/table/teams/teams-table-utils";
import { sphereColors } from "@styles/common-colors";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { FaroText } from "@faro-lotv/flat-ui";

type TeamCategoryChipColors = {
  /** The border color for the chip team */
  border: string;

  /** The background color for the chip team */
  background: string;

  /** The icon color for the chip team */
  icon: string;
};

interface TeamCategoryChipIconProps {
  /**
   * The category kind of the team.
   * Determines the styling and tooltip content.
   */
  kind: SphereDashboardAPITypes.TeamCategoryKind;

  /** If shows the text instead of a dot icon. */
  shouldShowText?: boolean;
}

/**
 * Returns a color configuration object for rendering a chip based on a team category kind.
 */
function getTeamCategoryChipColors(
  categoryKind: SphereDashboardAPITypes.TeamCategoryKind
): TeamCategoryChipColors {
  switch (categoryKind) {
    case SphereDashboardAPITypes.TeamCategoryKind.emailDomains:
    default:
      return {
        border: sphereColors.purple100,
        background: sphereColors.purple50,
        icon: sphereColors.purple400,
      };
  }
}

/**
 * Returns the description for the tooltip.
 */
function getDescriptionByTeamCategoryKind(
  categoryKind: SphereDashboardAPITypes.TeamCategoryKind
): string | undefined {
  switch (categoryKind) {
    case SphereDashboardAPITypes.TeamCategoryKind.emailDomains:
      return "Users logging in through the configured SSO connection are automatically added here with default permissions.";
  }
}

/**
 * Renders a category chip for a team, with optional label text and tooltip.
 *
 * - If `shouldShowText` is provided, the chip shows text in uppercase and pill-shaped.
 * - If `shouldShowText` is not provided, a small colored circle is shown instead.
 * - On hover, a tooltip provides information about the team category.
 */
export function TeamCategoryChipIcon({
  kind,
  shouldShowText = false,
}: TeamCategoryChipIconProps): JSX.Element {
  const chipColors = getTeamCategoryChipColors(kind);
  const label = getLabelByTeamCategoryKind(kind);

  return (
    <SphereTooltip
      title={
        <Stack gap={"0.5rem"} padding={"0.375rem 0.75rem"}>
          <FaroText variant="bodyS" color={sphereColors.pureWhite}>
            {label}
          </FaroText>
          <FaroText variant="bodyS" color={sphereColors.pureWhite}>
            {getDescriptionByTeamCategoryKind(kind)}
          </FaroText>
        </Stack>
      }
      tooltipProps={{
        slotProps: {
          tooltip: {
            sx: { backgroundColor: sphereColors.gray950 },
          },
        },
      }}
      enterDelay={300}
    >
      <Box
        sx={{
          height: "1.5rem",
          width: "fit-content",
          borderRadius: shouldShowText ? "1rem" : "50%",
          display: "flex",
          alignItems: "center",
          border: `1px solid ${chipColors.border}`,
          backgroundColor: chipColors.background,
          marginRight: shouldShowText ? 0 : "1rem",
          padding: "0 0.5rem",
        }}
      >
        {shouldShowText ? (
          <FaroText
            variant="labelS"
            color={chipColors.icon}
            textTransform={"uppercase"}
          >
            {label}
          </FaroText>
        ) : (
          <CircleIcon
            data-testid="CircleIcon"
            sx={{
              color: chipColors.icon,
              width: "0.5rem",
              height: "0.5rem",
            }}
          />
        )}
      </Box>
    </SphereTooltip>
  );
}
